import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Trash2, Pencil, X, Sparkles, Loader2, PieChart as PieIcon, Upload } from "lucide-react";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { toast } from "sonner";
import api, { apiErrorDetail } from "../lib/api";
import { fmtPrice, fmtNum, fmtPct, changeClass } from "../lib/format";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { CsvImportDialog } from "../components/CsvImportDialog";

const PALETTE = ["#2563eb", "#10b981", "#eab308", "#ef4444", "#8b5cf6", "#06b6d4", "#f97316", "#ec4899", "#64748b", "#84cc16"];

const usd = (v) => (v == null ? "—" : `${v < 0 ? "-" : ""}$${Math.abs(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

const esc = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

const Donut = ({ title, data, nameKey, testId }) => (
  <div className="widget p-4" data-testid={testId}>
    <div className="overline-label mb-2 flex items-center gap-2"><PieIcon className="w-3.5 h-3.5" />{title}</div>
    <ResponsiveContainer width="100%" height={240}>
      <PieChart>
        <Pie data={data} dataKey="value_usd" nameKey={nameKey} innerRadius={55} outerRadius={85} stroke="#0a0a0a" strokeWidth={2}>
          {data.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
        </Pie>
        <Tooltip contentStyle={{ background: "#1a1a1a", border: "1px solid #27272a", borderRadius: 2, fontFamily: "IBM Plex Mono", fontSize: 12 }}
          formatter={(v, n, p) => [`${usd(v)} (${p.payload.weight_pct}%)`, p.payload[nameKey]]} />
        <Legend wrapperStyle={{ fontSize: 11, fontFamily: "IBM Plex Mono" }} />
      </PieChart>
    </ResponsiveContainer>
  </div>
);

const StatCard = ({ label, value, sub, subClass, testId }) => (
  <div className="widget p-4" data-testid={testId}>
    <div className="overline-label">{label}</div>
    <div className="font-mono text-xl text-white mt-2">{value}</div>
    {sub && <div className={`font-mono text-xs mt-1 ${subClass || "text-muted-foreground"}`}>{sub}</div>}
  </div>
);

const HoldingDialog = ({ open, onOpenChange, universe, initial, onSubmit }) => {
  const isEdit = !!initial?.id;
  const [form, setForm] = useState({ ticker: "", shares: "", buy_price: "", buy_date: "" });

  useEffect(() => {
    if (open) {
      setForm(initial
        ? { ticker: initial.ticker, shares: String(initial.shares), buy_price: String(initial.buy_price), buy_date: initial.buy_date }
        : { ticker: "", shares: "", buy_price: "", buy_date: new Date().toISOString().slice(0, 10) });
    }
  }, [open, initial]);

  const selected = universe.find((u) => u.ticker === form.ticker);
  const submit = () => {
    if (!form.ticker || !form.shares || !form.buy_price || !form.buy_date) return;
    onSubmit({ ticker: form.ticker, shares: Number(form.shares), buy_price: Number(form.buy_price), buy_date: form.buy_date });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm bg-card border-border sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight">{isEdit ? `Edit ${initial.ticker} lot` : "Add holding"}</DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground">
            Buy price in the ticker's native quote units — pence for London (.L) tickers.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {!isEdit && (
            <div className="space-y-1.5">
              <Label className="overline-label">Ticker</Label>
              <Select value={form.ticker} onValueChange={(v) => {
                const u = universe.find((x) => x.ticker === v);
                setForm({ ...form, ticker: v, buy_price: u?.price ? String(u.price) : form.buy_price });
              }}>
                <SelectTrigger className="rounded-sm bg-background h-10" data-testid="holding-ticker-select">
                  <SelectValue placeholder="Select from universe" />
                </SelectTrigger>
                <SelectContent className="rounded-sm max-h-72">
                  {universe.map((u) => (
                    <SelectItem key={u.ticker} value={u.ticker}>
                      <span className="font-mono">{u.ticker}</span> · {u.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="overline-label">Shares</Label>
              <Input type="number" min="0" step="any" className="rounded-sm bg-background font-mono" data-testid="holding-shares-input"
                value={form.shares} onChange={(e) => setForm({ ...form, shares: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="overline-label">Buy price {selected?.currency === "GBp" ? "(pence)" : ""}</Label>
              <Input type="number" min="0" step="any" className="rounded-sm bg-background font-mono" data-testid="holding-price-input"
                value={form.buy_price} onChange={(e) => setForm({ ...form, buy_price: e.target.value })} />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="overline-label">Buy date</Label>
            <Input type="date" className="rounded-sm bg-background font-mono" data-testid="holding-date-input"
              value={form.buy_date} onChange={(e) => setForm({ ...form, buy_date: e.target.value })} />
          </div>
          <Button onClick={submit} className="w-full rounded-sm bg-primary hover:bg-primary/85 text-white" data-testid="holding-submit-button">
            {isEdit ? "Save changes" : "Add lot"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const ExplainMovePanel = ({ portfolioId }) => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/portfolios/${portfolioId}/explain-move`);
      setResult(data);
    } catch (e) {
      toast.error(apiErrorDetail(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="widget p-6 bg-blue-950/20 border-primary/20" data-testid="explain-move-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-primary" />
          <h3 className="font-heading font-bold text-lg text-white tracking-tight">Why did my portfolio move today?</h3>
        </div>
        {result && (
          <span className="text-[11px] font-mono text-muted-foreground">
            {result.cached ? "CACHED · " : "FRESH · "}{new Date(result.generated_at).toLocaleTimeString()}
          </span>
        )}
      </div>
      <p className="text-sm text-muted-foreground mt-1 mb-4">
        Claude attributes today's move to your holdings' real day changes and headlines.
      </p>
      {!result ? (
        <Button onClick={run} disabled={loading} className="rounded-sm bg-primary hover:bg-primary/85 text-white" data-testid="explain-move-button">
          {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Attributing move…</> : <><Sparkles className="w-4 h-4 mr-2" /> Explain today's move</>}
        </Button>
      ) : (
        <div className="space-y-3 fade-up" data-testid="explain-move-content">
          {result.explanation.split("\n").map((line, i) => (
            <p key={i} className={`text-sm text-gray-100 leading-relaxed ${line.trim().startsWith("- ") ? "pl-3" : ""}`}
              dangerouslySetInnerHTML={{ __html: esc(line).replace(/\*\*(.+?)\*\*/g, '<strong class="text-white">$1</strong>') || "&nbsp;" }} />
          ))}
        </div>
      )}
      <p className="text-xs text-muted-foreground mt-5 pt-3 border-t border-white/10">
        This is AI-generated research, not financial advice.
      </p>
    </div>
  );
};

export default function Portfolio() {
  const [portfolios, setPortfolios] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [view, setView] = useState(null);
  const [universe, setUniverse] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [pName, setPName] = useState("");
  const [holdingDialog, setHoldingDialog] = useState({ open: false, initial: null });
  const [csvOpen, setCsvOpen] = useState(false);

  const loadPortfolios = useCallback(async () => {
    const { data } = await api.get("/portfolios");
    setPortfolios(data.portfolios);
    return data.portfolios;
  }, []);

  const loadView = useCallback(async (id) => {
    const { data } = await api.get(`/portfolios/${id}`);
    setView(data);
  }, []);

  useEffect(() => {
    loadPortfolios().then((l) => l.length && setActiveId(l[0].id));
    api.get("/market/universe").then((r) => setUniverse(r.data.stocks));
  }, [loadPortfolios]);

  useEffect(() => {
    if (activeId) loadView(activeId);
    else setView(null);
  }, [activeId, loadView]);

  const createPortfolio = async () => {
    if (!pName.trim()) return;
    const { data } = await api.post("/portfolios", { name: pName.trim() });
    setCreateOpen(false);
    setPName("");
    await loadPortfolios();
    setActiveId(data.id);
  };

  const deletePortfolio = async (id) => {
    await api.delete(`/portfolios/${id}`);
    const l = await loadPortfolios();
    if (activeId === id) setActiveId(l[0]?.id || null);
    toast.success("Portfolio deleted");
  };

  const submitHolding = async (payload) => {
    try {
      if (holdingDialog.initial?.id) {
        await api.patch(`/portfolios/${activeId}/holdings/${holdingDialog.initial.id}`, payload);
        toast.success("Lot updated");
      } else {
        await api.post(`/portfolios/${activeId}/holdings`, payload);
        toast.success(`${payload.ticker} lot added`);
      }
      setHoldingDialog({ open: false, initial: null });
      loadView(activeId);
      loadPortfolios();
    } catch (e) {
      toast.error(apiErrorDetail(e));
    }
  };

  const removeLot = async (hid) => {
    await api.delete(`/portfolios/${activeId}/holdings/${hid}`);
    loadView(activeId);
    loadPortfolios();
  };

  const t = view?.totals;

  return (
    <div className="space-y-6 fade-up">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="overline-label mb-2">Positions &amp; performance</div>
          <h1 className="font-heading font-black tracking-tighter text-4xl sm:text-5xl text-white">Portfolio</h1>
        </div>
        <Button onClick={() => setCreateOpen(true)} data-testid="create-portfolio-button"
          className="rounded-sm bg-primary hover:bg-primary/85 text-white gap-2">
          <Plus className="w-4 h-4" /> New portfolio
        </Button>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="portfolio-tabs">
        {portfolios.map((p) => (
          <div key={p.id} className={`group flex items-center border rounded-sm transition-colors duration-150 ${
            activeId === p.id ? "border-primary bg-primary/10" : "border-border hover:bg-white/5"}`}>
            <button onClick={() => setActiveId(p.id)} data-testid={`portfolio-tab-${p.id}`}
              className={`px-4 py-2 text-sm ${activeId === p.id ? "text-white" : "text-muted-foreground"}`}>
              {p.name} <span className="font-mono text-xs text-muted-foreground ml-1">{p.holdings_count}</span>
            </button>
            <button onClick={() => deletePortfolio(p.id)} data-testid={`delete-portfolio-${p.id}`}
              className="pr-2 text-muted-foreground/40 hover:text-loss transition-colors duration-150">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
        {portfolios.length === 0 && (
          <p className="text-sm text-muted-foreground" data-testid="no-portfolios-message">No portfolios yet — create one to start tracking.</p>
        )}
      </div>

      {view && (
        <>
          {/* Totals */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="portfolio-totals">
            <StatCard label="Total value" value={usd(t.value_usd)} sub={`cost ${usd(t.cost_usd)}`} testId="total-value-card" />
            <StatCard label="Unrealised P/L" value={usd(t.pl_usd)} sub={fmtPct(t.pl_pct)}
              subClass={changeClass(t.pl_usd)} testId="total-pl-card" />
            <StatCard label="Day P/L" value={usd(t.day_pl_usd)} sub={fmtPct(t.day_pl_pct)}
              subClass={changeClass(t.day_pl_usd)} testId="day-pl-card" />
            <StatCard label="FX GBP/USD" value={view.fx_gbpusd ? fmtNum(view.fx_gbpusd, 4) : "—"}
              sub=".L pence ÷100 → GBP → USD" testId="fx-card" />
          </div>

          <ExplainMovePanel portfolioId={view.id} />

          {/* Positions */}
          <div className="widget overflow-hidden" data-testid="positions-table">
            <div className="px-4 py-3 border-b border-border flex items-center justify-between">
              <span className="overline-label">Positions</span>
              <div className="flex gap-2">
                <Button onClick={() => setCsvOpen(true)} data-testid="import-csv-button" variant="outline"
                  className="rounded-sm border-border h-8 text-xs gap-1.5">
                  <Upload className="w-3.5 h-3.5" /> Import CSV
                </Button>
                <Button onClick={() => setHoldingDialog({ open: true, initial: null })} data-testid="add-holding-button"
                  className="rounded-sm bg-primary hover:bg-primary/85 text-white h-8 text-xs gap-1.5">
                  <Plus className="w-3.5 h-3.5" /> Add holding
                </Button>
              </div>
            </div>
            {view.positions.length === 0 ? (
              <p className="p-8 text-sm text-muted-foreground" data-testid="empty-portfolio-message">No holdings yet — add your first lot.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border text-left">
                      {["Ticker", "Sector", "Shares", "Avg cost", "Price", "Value $", "P/L $", "P/L %", "Day", "Weight"].map((h, i) => (
                        <th key={h} className={`px-3 py-2.5 overline-label whitespace-nowrap ${i >= 2 ? "text-right" : ""}`}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="font-mono">
                    {view.positions.map((p) => (
                      <tr key={p.ticker} className="border-b border-border/50 even:bg-white/[0.02]" data-testid={`position-row-${p.ticker}`}>
                        <td className="px-3 py-3 font-medium whitespace-nowrap">
                          <Link to={`/stock/${p.ticker}`} className="text-primary hover:underline">{p.ticker}</Link>
                        </td>
                        <td className="px-3 py-3 text-xs text-muted-foreground font-body whitespace-nowrap">{p.sector}</td>
                        <td className="px-3 py-3 text-right text-gray-300">{fmtNum(p.shares, 0)}</td>
                        <td className="px-3 py-3 text-right text-gray-300">{fmtPrice(p.avg_cost, p.currency)}</td>
                        <td className="px-3 py-3 text-right text-white">{fmtPrice(p.current_price, p.currency)}</td>
                        <td className="px-3 py-3 text-right text-white">{usd(p.value_usd)}</td>
                        <td className={`px-3 py-3 text-right ${changeClass(p.pl_usd)}`}>{usd(p.pl_usd)}</td>
                        <td className={`px-3 py-3 text-right ${changeClass(p.pl_usd)}`}>{fmtPct(p.pl_pct)}</td>
                        <td className={`px-3 py-3 text-right ${changeClass(p.day_change_pct)}`}>{fmtPct(p.day_change_pct)}</td>
                        <td className="px-3 py-3 text-right text-gray-300">{fmtNum(p.weight_pct, 1)}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Allocations */}
          {view.positions.length > 0 && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Donut title="Allocation by sector" data={view.allocation_by_sector} nameKey="sector" testId="allocation-sector-chart" />
              <Donut title="Allocation by holding" data={view.allocation_by_holding} nameKey="ticker" testId="allocation-holding-chart" />
            </div>
          )}

          {/* Lots */}
          {view.lots.length > 0 && (
            <div className="widget overflow-hidden" data-testid="lots-table">
              <div className="px-4 py-3 border-b border-border">
                <span className="overline-label">Lots ({view.lots.length})</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border text-left">
                      {["Ticker", "Buy date", "Shares", "Buy price", "Cost $", "Value $", "P/L $", ""].map((h, i) => (
                        <th key={i} className={`px-3 py-2.5 overline-label whitespace-nowrap ${i >= 2 && i <= 6 ? "text-right" : ""}`}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="font-mono">
                    {view.lots.map((l) => (
                      <tr key={l.id} className="border-b border-border/50 even:bg-white/[0.02]" data-testid={`lot-row-${l.id}`}>
                        <td className="px-3 py-2.5 text-primary">{l.ticker}</td>
                        <td className="px-3 py-2.5 text-gray-300">{l.buy_date}</td>
                        <td className="px-3 py-2.5 text-right text-gray-300">{fmtNum(l.shares, 0)}</td>
                        <td className="px-3 py-2.5 text-right text-gray-300">{fmtPrice(l.buy_price, l.currency)}</td>
                        <td className="px-3 py-2.5 text-right text-gray-300">{usd(l.cost_usd)}</td>
                        <td className="px-3 py-2.5 text-right text-white">{usd(l.value_usd)}</td>
                        <td className={`px-3 py-2.5 text-right ${changeClass(l.pl_usd)}`}>{usd(l.pl_usd)} ({fmtPct(l.pl_pct)})</td>
                        <td className="px-3 py-2.5 text-right whitespace-nowrap">
                          <button onClick={() => setHoldingDialog({ open: true, initial: l })} data-testid={`edit-lot-${l.id}`}
                            className="p-1.5 text-muted-foreground hover:text-primary transition-colors duration-150"><Pencil className="w-3.5 h-3.5" /></button>
                          <button onClick={() => removeLot(l.id)} data-testid={`delete-lot-${l.id}`}
                            className="p-1.5 text-muted-foreground hover:text-loss transition-colors duration-150"><X className="w-4 h-4" /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="px-4 py-2.5 text-[11px] text-muted-foreground border-t border-border">{view.pence_note}</p>
            </div>
          )}
        </>
      )}

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="rounded-sm bg-card border-border sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight">New portfolio</DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground">Track lots and performance for a set of holdings.</DialogDescription>
          </DialogHeader>
          <Input autoFocus placeholder="e.g. Core long-term" className="rounded-sm bg-background" data-testid="portfolio-name-input"
            value={pName} onChange={(e) => setPName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && createPortfolio()} />
          <Button onClick={createPortfolio} disabled={!pName.trim()} data-testid="portfolio-create-confirm"
            className="rounded-sm bg-primary hover:bg-primary/85 text-white">Create</Button>
        </DialogContent>
      </Dialog>

      <HoldingDialog open={holdingDialog.open} onOpenChange={(o) => setHoldingDialog({ open: o, initial: o ? holdingDialog.initial : null })}
        universe={universe} initial={holdingDialog.initial} onSubmit={submitHolding} />

      {activeId && (
        <CsvImportDialog open={csvOpen} onOpenChange={setCsvOpen} portfolioId={activeId}
          onImported={() => { loadView(activeId); loadPortfolios(); }} />
      )}
    </div>
  );
}
